import {WizardStepConfig} from 'platform/components';

import {ReactElement, createElement} from 'react';
import {useWatch} from 'react-hook-form';

import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {useDebouncedWatch} from '../../kit/hooks/useDebouncedWatch';
import {useWizardIndicators} from '../../kit/hooks/useWizardIndicators';
import {StepBoundary} from '../../kit/WizardStepContext';

const stepIds = ['basicInformation', 'vehicleData', 'features'] as const;
// TODO-SF: This is a temporary solution to get the wizard steps working, will be refactored
// to use Mandatory fields - https://carvago.atlassian.net/browse/T20-86159
export const useBaseVehicleWizard = (
  // control: Control<BaseVehicle>,
  stepContents: {
    basicInformation: ReactElement;
    vehicleData: ReactElement;
    features: ReactElement;
  }
): WizardStepConfig[] => {
  // Watch features immediately (dropdown/checkbox selections)
  const features = useWatch({
    // RHF does not support unions in control generics, for example: Control<BaseVehicle | RentalVehicle> or event Control<BaseVehicle> | Control<RentalVehicle>
    // for now I remove the control from here, so useWatch always search for nearest control provider
    // control,
    name: 'features',
  });

  // Debounce title and mileage to reduce performance impact when typing
  const title = useDebouncedWatch(undefined, 'title', 300);
  const mileage = useDebouncedWatch(undefined, 'mileage', 300);

  const indicators = useWizardIndicators({stepIds});

  return [
    {
      title: i18n.t('general.labels.basicInformation'),
      content: createElement(StepBoundary, {id: 'basicInformation'}, stepContents.basicInformation),
      subtitle: title || i18n.t('general.labels.notSpecified'),
      severity: indicators.basicInformation?.severity,
      isBold: true,
    },
    {
      title: i18n.t('entity.vehicle.labels.vehicleData'),
      content: createElement(StepBoundary, {id: 'vehicleData'}, stepContents.vehicleData),
      subtitle: isNotNil(mileage)
        ? `${mileage} ${i18n.t('general.metric.km')}`
        : i18n.t('general.labels.notSpecified'),
      severity: indicators.vehicleData?.severity,
      isBold: true,
    },
    {
      title: i18n.t('entity.vehicle.labels.features'),
      content: createElement(StepBoundary, {id: 'features'}, stepContents.features),
      subtitle: !isNilOrEmpty(features)
        ? `${features?.length} ${i18n.t('entity.vehicle.labels.selectedFeatures')}`
        : i18n.t('entity.vehicle.labels.noFeaturesSelected'),
      severity: indicators.features?.severity,
      isBold: true,
    },
  ];
};
